import React from 'react';

import { SimpleTextInput } from '@components/admin/Forms';
import { Button } from '@components/Button';
import { Layout } from '@components/Layout';
import { Pagination } from '@components/Pagination';
import { QueryContainer } from '@components/QueryContainer';
import { cellFactory, Table } from '@components/Table/Table';
import { isInstructionOverdue } from '@components/utilities/Bookings/helpers';
import { VerticalSpace } from '@components/VerticalSpace';
import { getInstructionStatusLabel, instructionStrings } from '@data/instruction';
import { useDeliveryInstructionList } from '@hooks/queries/useDeliveryInstructionList';
import { useSearch } from '@hooks/useSearch';
import { deliveryInstructionListSearchType } from '@models/DeliveryInstruction';
import { getLastVisibleStatus, instructionStatusName } from '@models/Instruction';
import { useCommonStyles, makeMuiStyles, useIsMobile } from '@styles';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import { url } from '@utils/pages';
import { stringifySearch } from '@utils/types';
import clsx from 'clsx';
import { Form, Formik } from 'formik';
import { Link } from 'react-router-dom';

const strings = createStrings({
  ja: {
    d2dNo: 'D2D番号',
    all: 'すべて',
    no: 'NO.',
    etd: '出港予定日',
    eta: '入港予定日',
    toBookingDetails: '案件詳細へ',
    prompt:
      '*送信済の納品の最終確認を変更する場合、左下の「...」アイコンをクリックして、D2Dカスタマーセンターへご連絡ください。',
  },
});

const useStyles = makeMuiStyles((theme, common) => {
  return {
    link: {
      color: theme.palette.primary.main,
      whiteSpace: 'pre',
    },
    container: {
      [theme.breakpoints.up('lg')]: {
        padding: '1rem 2rem',
      },
      backgroundColor: theme.palette.background.paper,
      flex: 1,
    },
    inner: {
      ...common.flexColumn,
      maxWidth: '1600px',
      padding: '1rem 1rem',
      margin: '0 auto',
    },
    innerContent: {
      flex: 1,
      padding: '1rem 2rem',
      overflow: 'hidden',
      ...common.verticalSpace3,
    },
    searchContainer: {
      ...common.normalText,
      paddingTop: 23,
    },
    formContainer: {
      ...common.flexColumn,
      ...common.verticalSpace1,
    },
    paginationContainer: {
      ...common.flexColumn,
      flex: 1,
      justifyContent: 'flex-end',
      alignItems: 'flex-end',
    },
    startContainer: {
      ...common.flexInline,
      columnGap: '1rem',
      [theme.breakpoints.down('sm')]: {
        ...common.flexColumn,
        rowGap: '0.5rem',
      },
    },
    badge: {
      height: 8,
      width: 8,
      borderRadius: '50%',
      backgroundColor: theme.palette.warning.dark,
      position: 'absolute',
      top: -5,
      right: -5,
      paddingBottom: 2,
      zIndex: 2,
    },
    errorRow: {
      '& * ': {
        color: theme.palette.error.main,
      },
    },
  };
});

export const DeliveryInstructionList: React.FC = () => {
  const { search, updateSearch, clearSearch } = useSearch(deliveryInstructionListSearchType);
  const classes = { ...useStyles(), ...useCommonStyles() };
  const isMobile = useIsMobile();
  const deliveryInstructionList = useDeliveryInstructionList({
    ...search,
    status: search.status && search.status !== 'ALL' ? [search.status] : [],
  });

  const currentPath = window.location.pathname + window.location.search;
  const pageCount = Math.floor((deliveryInstructionList.data?.count ?? 0 - 1) / 10) + 1;

  return (
    <Layout
      title={instructionStrings.deliveryInstruction}
      breadcrumbs={['/utilities/Dashboard', '/utilities/Bookings/DeliveryInstructionList']}
    >
      <div className={classes.container}>
        <div className={classes.inner}>
          <div className={clsx(classes.pageTitleText, classes.textPrimary)}>
            {instructionStrings.deliveryInstruction}
          </div>
          <div className={classes.searchContainer}>
            <Formik
              initialValues={{
                d2d_no: search.d2d_no,
                status: search.status ?? 'ALL',
              }}
              enableReinitialize
              onSubmit={async (values) => {
                updateSearch({
                  ...values,
                  page: 1,
                });
              }}
            >
              <Form className={classes.formContainer}>
                <div className={classes.startContainer}>
                  <SimpleTextInput
                    name="d2d_no"
                    label={strings.d2dNo}
                    labelProps={isMobile ? {} : { width: 60 }}
                  />
                  <SimpleTextInput
                    name="status"
                    label={instructionStrings.status}
                    choose
                    textInputWidth={139}
                    options={[
                      {
                        value: 'ALL',
                        label: strings.all,
                      },
                      ...instructionStatusName.types.map((t) => ({
                        value: t.value,
                        label: getInstructionStatusLabel(t.value, false),
                      })),
                    ]}
                    labelProps={isMobile ? {} : { width: 100 }}
                  />
                </div>
                <div className={classes.startContainer}>
                  <Button text={formStrings.filter} color="primary" type="submit" size="small" />

                  <Button
                    text={formStrings.reset}
                    color="primary"
                    type="button"
                    size="small"
                    onClick={clearSearch}
                  />

                  <div style={{ flex: 1 }} />
                </div>
              </Form>
            </Formik>
          </div>
          <VerticalSpace height={8} />
          <div className={clsx(classes.textPrimary, classes.middleText, classes.fontWeight700)}>
            {strings.prompt}
          </div>
          <VerticalSpace height={8} />
          <QueryContainer queries={[deliveryInstructionList]}>
            <Table>
              <Table.Head>
                <Table.Row
                  columns={[
                    cellFactory.create(strings.no),
                    cellFactory.create(strings.d2dNo),
                    cellFactory.create(instructionStrings.deadline),
                    cellFactory.create(instructionStrings.status),
                    cellFactory.create(strings.etd),
                    cellFactory.create(strings.eta),
                    cellFactory.create(''),
                  ]}
                />
              </Table.Head>
              <Table.Body>
                {deliveryInstructionList.data?.results?.map((item, index) => {
                  return (
                    <Table.Row
                      key={index}
                      className={
                        isInstructionOverdue(item.cutoff_date) &&
                        getLastVisibleStatus(item.status)?.name === '未送信'
                          ? classes.errorRow
                          : ''
                      }
                      columns={[
                        cellFactory.create(index + 1),
                        cellFactory.create(
                          <Link
                            to={`${url(
                              '/utilities/Bookings/DeliveryInstruction',
                            )}?${stringifySearch({ from: currentPath, d2dNo: item.d2d_no })}`}
                            className={clsx(classes.link, classes.underline)}
                            target="_blank"
                          >
                            {item.d2d_no}
                          </Link>,
                        ),
                        cellFactory.create(item.cutoff_date),
                        cellFactory.create(
                          getInstructionStatusLabel(
                            getLastVisibleStatus(item.status)?.name ?? '未送信',
                            false,
                          ),
                        ),
                        cellFactory.create(item.etd),
                        cellFactory.create(item.eta),
                        cellFactory.create(
                          <Link
                            to={`${url('/utilities/Bookings/CurrentBookingDetails')}?d2dNo=${
                              item.d2d_no
                            }`}
                            className={classes.link}
                          >
                            <span style={{ position: 'relative' }}>
                              {['未送信', '修正依頼済'].includes(
                                getLastVisibleStatus(item.status)?.name ?? '未送信',
                              ) && <div className={classes.badge} />}
                              {strings.toBookingDetails}
                            </span>
                          </Link>,
                        ),
                      ]}
                    />
                  );
                })}
              </Table.Body>
            </Table>
            <br />
            <div className={clsx(classes.flexInline, classes.justifyEnd)}>
              <Pagination
                page={Math.min(Math.max(search.page ?? 1, 1), pageCount) - 1}
                count={pageCount}
                onChange={(page) => updateSearch({ page: page + 1 })}
              />
            </div>
          </QueryContainer>
        </div>
      </div>
    </Layout>
  );
};
